/**
 * Initializes an Alpine.js data object to manage product variants, including selection, visibility, and cart operations across various components. This component facilitates complex product interactions on e-commerce platforms.
 *
 * Key Features:
 * 1. Variant Management: Handles logic for selecting and displaying product variants and options, adjusting for products with standalone or bundled variants.
 * 2. Dynamic Option Visibility: Automatically hides or shows product options based on certain conditions, enhancing the user experience by simplifying choices when appropriate.
 * 3. Bundle Integration: Allows for the selection of product bundles alongside individual products, including logic to map selected options to corresponding bundle variants.
 * 4. Cross-Sell Functionality: Optionally integrates a cross-sell product, adding it to the cart along with the main product based on user interaction.
 * 5. Cart Interaction: Implements add-to-cart functionality, with detailed error handling and dynamic feedback to the user.
 * 6. Responsive to Changes: Listens for product changes when configured, updating the component state to reflect the new product data dynamically.
 * 7. Advanced Configuration: Uses custom events and a mix of real-time data checks to ensure the interface responds correctly to user interactions and data changes.
 *
 * Usage:
 * This component is used across several different parts of a web application where products with multiple variants are displayed. It is particularly useful in scenarios where products have complex option sets that may include color, size, or bundle selections. It supports standalone products, products with multiple variants, and bundled products, providing a comprehensive tool for product variant management.
 */

const optionsToHide = Object.freeze(['Color', 'Set']);
const forceManualOption = Object.freeze(['Size']);

const productVariantData = (product, listenForChanges = false, crossSellId = null) => ({
  product,
  bundle: product?.bundleProduct,
  backStock: product?.backStock,
  isBundleSelected: false,
  hasStandaloneVariant: product.standaloneVariant ? true : false,
  isStandaloneSelected: false,
  selectedNormalVariant: product.standaloneVariant ? product.variants[0] : null,
  options: product.options,
  selectedVariants: product.standaloneVariant ? [product.variants[0]] : product.variants,
  selectedOptions: Object.fromEntries(product.options.map((option) => [option.name, ''])),
  isWorking: false,
  crossSellChecked: false,
  crossSellVariant: null,
  loadingVariants: true,

  get standaloneVar() {
    return product.standaloneVariant;
  },

  get normalVar() {
    return product.variants[0];
  },
  
  get normalProID() {
    return product.productID;
  },

  get backStockStatus() {
    return this.backStock;
  },

  onStandaloneChange(isStandalone) {
    this.isStandaloneSelected = isStandalone;
    if (!this.isStandaloneSelected) {
      this.selectedVariants = [this.selectedNormalVariant];
    } else {
      this.selectedVariants = [this.standaloneVar];
    }
  },

  updateNormalVar(varId) {
    const matchingVariant = this.product.variants.find((variant) => variant.id === varId);
    if (matchingVariant) {
      this.selectedNormalVariant = matchingVariant;
      this.selectedVariants = [this.selectedNormalVariant];
    } else {
      console.warn(`Variant with id ${varId} not found.`);
    }
  },

  get optionsToHide() {
    const totalVariants = this.selectedVariants.length;
    return Object.fromEntries(
      this.options
        .filter(
          (option) =>
            (totalVariants === 1 && !forceManualOption.includes(option.name)) ||
            (optionsToHide.includes(option.name) &&
              option.values.length === 1 &&
              this.selectedOptions[option.name] === option.values[0]),
        )
        .map((option) => [option.name, true]),
    );
  },

  get visibleOptions() {
    const hiddenOptions = this.optionsToHide;
    return this.options.filter((option) => !hiddenOptions.hasOwnProperty(option.name));
  },

  selectSecondOption() {
    if (this.selectSecondVariant) {
      if (this.options.length === 1 && this.options[0].values.length === 2) {
        this.selectedOptions[this.options[0].name] = this.options[0].values[1];
        this.normalizeOptionsAndVariants(Object.entries(this.selectedOptions));
      }
      return;
    }
    return;
  },

  mapSelectedOptionsToBundle() {
    if (!this.bundle || !this.bundle.variants.length) return null;
    const selectedOptionValue = this.currentVariant.selectedOptions[0].value;
    const matchingBundleVariant = this.bundle.variants.find((variant) => variant.title === selectedOptionValue);
    return matchingBundleVariant || null;
  },

  get finalSelectedVariantV() {
    return (this.currentVariant.variantProperties.title == "Outlet Vista™ Only" || 
        this.currentVariant.variantProperties.title == "Vista™ Only") 
       ? product.variants[0] 
       : this.currentVariant;
  },
  
  get finalSelectedVariant() {
    return this.isBundleSelected ? this.mapSelectedOptionsToBundle() : this.currentVariant;
  },

  get buyButtonPrice() {
    if (!this.crossSellChecked) {
      return simplePrice(this.finalSelectedVariant.formattedPrice);
    } else {
      const combinedPrice = Number(this.finalSelectedVariant.price) + Number(this.crossSellVariant.price.amount);
      const formattedCombined = formatPrice(this.crossSellVariant.price.currencyCode, combinedPrice);
      return simplePrice(formattedCombined);
    }
  },

  get buyButtonCaPrice() {
    if (!this.finalSelectedVariant.compareAtPrice && !this.crossSellVariant?.compareAtPrice) {
      return null;
    } else if (this.finalSelectedVariant.compareAtPrice && this.crossSellVariant?.compareAtPrice) {
      if (!this.crossSellChecked) {
        return simplePrice(this.finalSelectedVariant.formattedCompareAtPrice);
      } else {
        const combinedPrice = Number(this.finalSelectedVariant.price) + Number(this.crossSellVariant?.price.amount);
        const formattedCombined = formatPrice(this.crossSellVariant?.price.currencyCode, combinedPrice);
        return simplePrice(formattedCombined);
      }
    } else if (!this.finalSelectedVariant.compareAtPrice && this.crossSellVariant?.compareAtPrice) {
      if (!this.crossSellChecked) {
        return simplePrice(this.finalSelectedVariant.formattedPrice);
      } else {
        const combinedPrice =
          Number(this.finalSelectedVariant.price) + Number(this.crossSellVariant?.compareAtPrice.amount);
        const formattedCombined = formatPrice(this.crossSellVariant?.compareAtPrice.currencyCode, combinedPrice);
        return simplePrice(formattedCombined);
      }
    } else if (this.finalSelectedVariant.compareAtPrice && !this.crossSellVariant?.compareAtPrice) {
      if (!this.crossSellChecked) {
        return simplePrice(this.finalSelectedVariant.formattedCompareAtPrice);
      } else {
        const combinedPrice =
          Number(this.finalSelectedVariant.compareAtPrice) + Number(this.crossSellVariant?.price.amount);
        const formattedCombined = formatPrice(this.crossSellVariant?.price.currencyCode, combinedPrice);
        return simplePrice(formattedCombined);
      }
    }
  },

  get currentVariant() {
    if (this.selectedVariants[0]) {
      return this.selectedVariants[0];
    }
    return null;
  },

  discountPercentage(variant) {
    if (variant.hasReducedPrice && variant.compareAtPrice) {
      return getSavingsPercentage(variant.compareAtPrice, variant.price);
    } else return null;
  },
  
  discountPercentageA(variant) {
    if (variant.hasReducedPrice && variant.compareAtPrice) {
      return  window.currency+""+Math.round(variant.compareAtPrice - variant.price);
    } else return null;
  },

  get bundleOptions() {
    const selectedVariantOption = {
      ...this.currentVariant,
      optionTitle: 'Hammock Only',
      optionSubtext: 'Ultra Comfortable Hammock',
      optionImage: this.product.media[0],
      isBundle: false,
    };
    let bundleVariantOption = {
      optionTitle: 'Hammock + Stand',
      optionSubtext: 'Adjustable, portable Hammock Stand',
      optionImage: this.bundle ? { src: this.bundle.firstImage.originalSrc } : undefined,
      isBundle: true,
    };
    if (this.bundle && this.bundle.variants.length === 1) {
      bundleVariantOption = {
        ...this.bundle.variants[0],
        ...bundleVariantOption,
      };
    } else if (this.bundle && this.bundle.variants.length > 1) {
      const matchedBundleVariant = this.mapSelectedOptionsToBundle();
      if (matchedBundleVariant) {
        bundleVariantOption = {
          ...matchedBundleVariant,
          ...bundleVariantOption,
        };
      } else {
        bundleVariantOption = null;
      }
    } else {
      bundleVariantOption = null;
    }
    return [selectedVariantOption, bundleVariantOption].filter((option) => option !== null);
  },

  onBundleChange(isBundle) {
    this.isBundleSelected = isBundle;
  },

  get optionsWithState() {
    const { options, selectedOptions } = this;
  
    return options.map((option) => {
      const { name, values } = option;
      return {
        name,
        values: values.map((value) => {
          const newSelectedOptions = { ...selectedOptions };
          delete newSelectedOptions[name];
          const newSelectedOptionsArray = [[name, value], ...Object.entries(newSelectedOptions)];
          const { availableVariants } = this.calculateOptionsAndVariants(newSelectedOptionsArray); 
          let colorSwatch = null;
          let badgeText = null;
          let badgeColor = null;
          let badgeTextColor = null;
          if (this.product?.multiSwatch) {
            const matchingSwatch = this.product.multiSwatch
              .find(swatch => swatch.title.toLowerCase() === name.toLowerCase() 
              && swatch.secondary_title.toLowerCase() === value.toLowerCase());
            if (matchingSwatch) {
              colorSwatch = matchingSwatch.swatch_image_reference;
              badgeText = matchingSwatch?.swatch_badge_text ? matchingSwatch.swatch_badge_text : null;
              badgeColor = matchingSwatch?.badge_color ? matchingSwatch.badge_color : null;
              badgeTextColor = matchingSwatch?.badge_text_color ? matchingSwatch.badge_text_color : null;
            };
          }
          let valueImage = null;
          let description = null;
          let showPrice = true;
          if (this.product?.optionProps) {
            console.log('optpropser', this.product.optionProps)
            const matchingOptionValue = this.product?.optionProps
            .find(obj => obj.option_title.toLowerCase() === name.toLowerCase() 
            && obj.value_title.toLowerCase() === value.toLowerCase());
            if (matchingOptionValue) {
              valueImage = matchingOptionValue.image;
              description = matchingOptionValue.description;
              showPrice = matchingOptionValue.show_total_price === 'true' ? true : false;
            };
        }
          return {
            value,
            variants: availableVariants.length,
            variantsInStock: availableVariants.filter((variant) => variant.availableForSale).length,
            colorSwatch,
            valueImage,
            description,
            showPrice,
            badgeText,
            badgeColor,
            badgeTextColor
          };
        }),
      };
    });
  },

  changeToVarImage() {
    if (this.currentVariant.image) {
      this.setSlideByImageUrl(this.currentVariant.image);
    }
  },

  variantMatcher(optionName, value) {
    // Start from the normalized selectedOptions map
    const base = { ...this.selectedOptions, [optionName]: value };

    // Find a variant whose every selectedOptions pair matches our base
    const match = this.product.variants.find(variant =>
      variant.selectedOptions.every(vo => base[vo.name] === vo.value)
    );

    return match || null;  // may be unavailable; that’s fine
  },

  getPriceForOptionValue(optionName, value) {
    const matchingVariant = this.variantMatcher(optionName, value)
    return matchingVariant ? simplePrice(matchingVariant.formattedPrice) : null;
  },

  getCompPriceForOptionValue(optionName, value) {
    const matchingVariant = this.variantMatcher(optionName, value)
    return matchingVariant ? simplePrice(matchingVariant.formattedCompareAtPrice) : null;
  },

  getDescForOptionValue(optionName, value) {
    const matchingVariant = this.product.variants.find((variant) =>
      variant.selectedOptions.some((option) => option.name === optionName && option.value === value),
    );
    return matchingVariant ? matchingVariant.variantSubtext?.value : null;
  },

  discountPercentageV(optionName, value) {
    const matchingVariant = this.variantMatcher(optionName, value);
    if (!matchingVariant) return 0;
  
    const price = parseInt(matchingVariant.price || 0, 10);
    const compareAtPrice = parseInt(matchingVariant.compareAtPrice || 0, 10);
  
    if (compareAtPrice > price && compareAtPrice > 0) {
      const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
      return `${discount}%`;
    }
  
    return '0%';
  },

  discountPercentageAV(optionName, value) {
    const matchingVariant = this.variantMatcher(optionName, value);
    if (!matchingVariant) return 0;
  
    const price = parseInt(matchingVariant.price || 0, 10);
    const compareAtPrice = parseInt(matchingVariant.compareAtPrice || 0, 10);
  
    if (compareAtPrice > price && compareAtPrice > 0) {
      const discount = window.currency+""+Math.round(compareAtPrice - price);
      return `${discount}`;
    }
  
    return '0';
  },

  getAvailabilityForOptionValue(optionName, value) {
    const matchingVariant = this.variantMatcher(optionName, value);
    return matchingVariant ? matchingVariant.availableForSale : null;
  },

  getImageForOptionValue(optionName, value) {
    const matchingVariant = this.product.variants.find((variant) =>
      variant.selectedOptions.some((option) => option.name === optionName && option.value === value),
    );
    return matchingVariant ? matchingVariant.variantSelectorImage : null;
  },

  async init() {
    if (!product.standaloneVariant) {
      this.normalizeOptionsAndVariants(Object.entries(this.selectedOptions));
      this.selectSecondOption();
      if (listenForChanges) {
        window.addEventListener('current-product-change', (event) => {
          this.handleNewProduct(event);
        });
      }
    }
    if (crossSellId !== null) {
      const crossSellVar = await fetchLimitedVariantInfo(crossSellId);
      this.crossSellVariant = crossSellVar;
    }
    await this.$nextTick()
    this.loadingVariants = false;
  },

  async handleNewProduct(event) {
    this.product = event.detail.product;
    this.bundle = this.product?.bundleProduct;
    this.options = this.product?.options || [];
    this.isBundleSelected = false;
    this.selectedVariants = this.product?.variants || [];
    this.selectedOptions = Object.fromEntries(this.product.options.map((option) => [option.name, '']));
    await this.$nextTick();
    this.normalizeOptionsAndVariants(Object.entries(this.selectedOptions));
    this.selectSecondOption();
  },

  calculateOptionsAndVariants(selectedOptionsArray) {
  // Start from ALL variants (sold-out included)
  let candidateVariants = this.product.variants.slice();

  const correctSelectedOptions = {};

  for (const [name, value] of selectedOptionsArray) {
    const prev = candidateVariants;

    // Narrow by the chosen option (but still across all variants)
    candidateVariants = candidateVariants.filter(variant =>
      variant.selectedOptions.some(o => o.name === name && o.value === value)
    );

    if (candidateVariants.length === 0) {
      // Roll back if that choice leads to no variant
      candidateVariants = prev;

      // If this option is "forced manual" AND multiple remain, allow empty
      if (forceManualOption.includes(name) && candidateVariants.length > 1) {
        correctSelectedOptions[name] = '';
      } else {
        // Otherwise auto-pick the value from the first remaining variant
        const newValue = candidateVariants[0].selectedOptions.find(o => o.name === name)?.value;
        correctSelectedOptions[name] = newValue;

        // Re-narrow using that auto-picked value
        candidateVariants = candidateVariants.filter(variant =>
          variant.selectedOptions.some(o => o.name === name && o.value === newValue)
        );
      }
    } else {
      // Keep the user's selection
      correctSelectedOptions[name] = value;
    }
  }

  // Return the full candidate list (may include sold-out)
  return { availableVariants: candidateVariants, correctSelectedOptions };
},

  normalizeOptionsAndVariants(selectedOptionsArray) {
    const { availableVariants, correctSelectedOptions } = this.calculateOptionsAndVariants(selectedOptionsArray);
    this.selectedOptions  = correctSelectedOptions;
    this.selectedVariants = availableVariants;   // now includes sold-out matches as well
  },

  handleOptionChange(name, value) {
    const newSelectedOptions = { ...this.selectedOptions };
    delete newSelectedOptions[name];
    const newSelectedOptionsArray = [[name, value], ...Object.entries(newSelectedOptions)];
    console.log('newseloptsArray', newSelectedOptionsArray);
    this.normalizeOptionsAndVariants(newSelectedOptionsArray);
    console.log('selectedVariant', this.selectedVariants)
  },

  async addToCart(isDrawer = false) {
    if (this.isWorking) return;
    const { selectedVariants } = this;
    const [selectedVariant] = selectedVariants;
    const simpleId = selectedVariant.id.replace('gid://shopify/ProductVariant/', '');

    try {
      this.isWorking = true;

      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'post',
        body: JSON.stringify({ items: [{ id: simpleId }] }),
        headers: { 'content-type': 'application/json' },
      });

      if (!res.ok) {
        let description = '';
        try {
          const data = await res.json();
          description = data?.description;
        } catch (e) {
          //
        }
        throw new Error([`Failed to add to cart!`, description, `(status: ${res.status})`].filter(Boolean).join('\n'));
      }

      isDrawer && this.close();

      this.$dispatch('update-and-open-cart', {
        noOpenAfterUpdate: window.CURRENT_TEMPLATE === 'cart',
      });
    } catch (error) {
      alert(error.message);
      this.isWorking = false;
      throw error;
    }
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productVariantData', productVariantData);
});